import { TICKET_TOPICS } from '../../../../shared/ticketTopics.js'

/**
 * Turn the weekly rows from GET /api/tickets/topic-counts into one line per
 * topic: this week, last week, and the weekly average of the weeks before
 * this one (the baseline). Rows carry `week_start` as YYYY-MM-DD, Monday.
 *
 * `currentWeekStart` is the Monday of this week, YYYY-MM-DD. `baselineWeeks`
 * is how many full weeks before this one the average covers.
 */
export function summariseTopicCounts(rows, { currentWeekStart, lastWeekStart, baselineWeeks }) {
  const byTopic = new Map()
  for (const row of rows) {
    const key = row.topic ?? null
    const entry = byTopic.get(key) || { thisWeek: 0, lastWeek: 0, earlier: 0 }
    if (row.week_start === currentWeekStart) entry.thisWeek += row.count
    else {
      entry.earlier += row.count
      if (row.week_start === lastWeekStart) entry.lastWeek += row.count
    }
    byTopic.set(key, entry)
  }

  const lines = [...TICKET_TOPICS.map(topic => [topic.key, topic.label]), [null, 'No topic yet']]
    .map(([key, label]) => {
      const entry = byTopic.get(key) || { thisWeek: 0, lastWeek: 0, earlier: 0 }
      return { key, label, thisWeek: entry.thisWeek, lastWeek: entry.lastWeek, baseline: roundToTenth(entry.earlier / baselineWeeks) }
    })
    .filter(line => line.thisWeek || line.lastWeek || line.baseline)

  return lines.sort((a, b) => b.thisWeek - a.thisWeek || b.baseline - a.baseline)
}

/** Monday of the week holding `date`, and the Monday before it, as YYYY-MM-DD (UTC). */
export function weekStarts(date) {
  const daysSinceMonday = (date.getUTCDay() + 6) % 7
  const monday = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() - daysSinceMonday))
  const previous = new Date(monday.getTime() - 7 * 24 * 60 * 60 * 1000)
  return { currentWeekStart: isoDay(monday), lastWeekStart: isoDay(previous) }
}

function isoDay(date) {
  return date.toISOString().slice(0, 10)
}

function roundToTenth(value) {
  return Math.round(value * 10) / 10
}
